import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GroupedTagSelector, Modal, PopoverInfo, PopoverThreeDots, Tooltip } from "../../../components";
import { getDocGroupsAPI, getDocsFiltersAPI, updateDocsTagsAPI } from "../../../common/services";
import { useToaster } from "../../../common/contexts/toast";
import { errorMessageOf, failureMessageOf } from "../../../utils";
import PageLoader from "../../../components/page-loader";
import ManageFiltersPanel from "./manage-filters-panel";
import { WizardActionChoice, WizardDocsPanel, WizardRightPanel } from "./wizard-shared";
import { isOutdatedSelectionError, OUTDATED_SELECTION_MESSAGE } from "../utils";

// POST /docs/filters keys a group by its LABEL — `id` and `label` both hold the
// group's display name (elastic-docs-service.extractDocFacets) and no `system`
// flag is sent — so system groups are matched against the taxonomy's own system
// group names, normalized the way the backend groups them.
const normalizeLabel = (label) => String(label || "").trim().toLowerCase();

const groupLabelOf = (group) => group?.label || group?.id || "";

// One GroupedTagSelector group shape, whichever source the groups came from.
const buildSelectorData = (groups = [], getLabel, getOptions) =>
    groups.map((group) => {
        const label = getLabel(group);
        return {
            id: group.id,
            displayName: label,
            label,
            type: "multiselect",
            options: (getOptions(group) || []).map((option) => ({
                id: option.id,
                displayName: option.displayName,
                label: option.displayName,
            })),
        };
    });

// The taxonomy (GET /docs/groups), not search facets, so values with no tagged
// docs yet — e.g. ones just created in the manage dialog — still appear.
const buildTaxonomyData = (groups = []) =>
    buildSelectorData(groups, (group) => group.displayName, (group) => group.values);

// Shape the groups of POST /docs/filters into GroupedTagSelector data — same
// output as buildTaxonomyData, limited to what the selected files carry.
const buildDocFiltersData = (groups = []) =>
    buildSelectorData(groups, groupLabelOf, (group) => group.options);

const buildValueIdsByOption = (...groupLists) => {
    const map = new Map();
    groupLists.flat().forEach((group) => {
        (group?.options || []).forEach((option) => {
            if (!option?.id) return;
            map.set(option.id, [...new Set([...(option.valueIds || []), option.id].filter(Boolean))]);
        });
    });
    return map;
};

const hasAnySelection = (selections = {}) =>
    Object.values(selections).some((value) => (Array.isArray(value) ? value.length > 0 : !!value));

// Flatten a GroupedTagSelector value ({ groupId: [optionId|{id}] }) into unique ids.
const flattenSelectedIds = (selections = {}) => {
    const ids = [];
    Object.values(selections).forEach((value) => {
        const arr = Array.isArray(value) ? value : value ? [value] : [];
        arr.forEach((v) => {
            const id = typeof v === "object" ? v?.id : v;
            if (id) ids.push(id);
        });
    });
    return [...new Set(ids)];
};

// The chosen action titles the panel, names the confirm button and becomes the last breadcrumb.
const ACTION_OPTIONS = [
    { id: "replace",    lead: "Replace all",     rest: "current filters/attributes with new ones",  title: "Replace all filters/attributes",      applyLabel: "REPLACE" },
    { id: "add",        lead: "Add new",         rest: "filters/attributes to the current set",     title: "Add new filters/attributes",          applyLabel: "ADD" },
    { id: "remove",     lead: "Remove specific", rest: "filters/attributes from the current set",   title: "Remove specific filters/attributes",  applyLabel: "REMOVE" },
    { id: "remove-all", lead: "Remove all",      rest: "filters/attributes",                        title: "Remove all filters/attributes" },
];

const CONTINUE_ICON = "/images/icon/dotted-arrow.svg";

const getActionOption = (actionId) => ACTION_OPTIONS.find((option) => option.id === actionId);

export const getAffiliateActionTitle = (actionId) => getActionOption(actionId)?.title;

// Two-step wizard: step one (`action` = null) picks the action, step two the
// values. The page owns `action` because it also names a breadcrumb.
const AffiliateFiltersWizard = ({ docs = [], selection, count, action, onChangeAction, onApply, onManageApplied, onSelectionOutdated }) => {
    const { showToaster } = useToaster();
    const [draftAction, setDraftAction] = useState(action || null);
    const [systemSelections, setSystemSelections] = useState({});
    const [filterSelections, setFilterSelections] = useState({});
    const [attributeSelections, setAttributeSelections] = useState({});
    const [confirmRemoveAll, setConfirmRemoveAll] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);
    const [applying, setApplying] = useState(false);
    const [groups, setGroups] = useState([]);
    const [docFilters, setDocFilters] = useState({ key: null, filters: [], attributes: [] });

    // Going back to step one keeps the previous choice ticked.
    useEffect(() => {
        if (action) setDraftAction(action);
    }, [action]);

    // Load the full taxonomy (all groups + values, incl. unused ones).
    const loadGroups = useCallback(async () => {
        try {
            const res = await getDocGroupsAPI();
            setGroups(res?.data?.items || []);
        } catch (error) {
            console.error("[affiliate] failed to load groups", error);
            setGroups([]);
        }
    }, []);

    useEffect(() => {
        loadGroups();
    }, [loadGroups]);

    // Split the one taxonomy into the three selector sections: system groups
    // (backend-provided, not editable) and the customized groups by type.
    const systemGroups = useMemo(() => groups.filter((g) => g.system), [groups]);
    const customGroups = useMemo(() => groups.filter((g) => !g.system), [groups]);
    const filterGroups = useMemo(() => customGroups.filter((g) => g.type !== "attribute"), [customGroups]);
    const attributeGroups = useMemo(() => customGroups.filter((g) => g.type === "attribute"), [customGroups]);
    const systemData = useMemo(() => buildTaxonomyData(systemGroups), [systemGroups]);
    const filterData = useMemo(() => buildTaxonomyData(filterGroups), [filterGroups]);
    const attributeData = useMemo(() => buildTaxonomyData(attributeGroups), [attributeGroups]);

    const activeOption = getActionOption(action);
    const inValuesStep = !!activeOption;
    const isRemoveStep = action === "remove";
    const selectionKey = useMemo(() => JSON.stringify(selection || null), [selection]);

    useEffect(() => {
        if (!isRemoveStep) return undefined;

        let cancelled = false;
        getDocsFiltersAPI(JSON.parse(selectionKey))
            .then((res) => {
                if (cancelled) return;
                setDocFilters({ key: selectionKey, filters: res?.data?.filters || [], attributes: res?.data?.attributes || [] });
            })
            .catch((error) => {
                console.error("[affiliate] failed to load the files' filters", error);
                if (!cancelled) setDocFilters({ key: selectionKey, filters: [], attributes: [] });
            });

        return () => { cancelled = true; };
    }, [isRemoveStep, selectionKey]);

    const docFiltersLoading = (docFilters.key !== selectionKey);

    // System groups arrive in the same `filters` list as the customized ones.
    const systemGroupLabels = useMemo(
        () => new Set(systemGroups.map((group) => normalizeLabel(group.displayName))),
        [systemGroups]
    );
    const isSystemFacet = useCallback(
        (group) => (
            String(group?.id || "").startsWith("system:")
            || systemGroupLabels.has(normalizeLabel(groupLabelOf(group)))
        ),
        [systemGroupLabels]
    );
    const docSystemGroups = useMemo(() => docFilters.filters.filter(isSystemFacet), [docFilters, isSystemFacet]);
    const docFilterGroups = useMemo(() => docFilters.filters.filter((g) => !isSystemFacet(g)), [docFilters, isSystemFacet]);
    const removeSystemData = useMemo(() => buildDocFiltersData(docSystemGroups), [docSystemGroups]);
    const removeFilterData = useMemo(() => buildDocFiltersData(docFilterGroups), [docFilterGroups]);
    const removeAttributeData = useMemo(() => buildDocFiltersData(docFilters.attributes), [docFilters]);

    // One option can stand for several tag values — keep the mapping for apply.
    const valueIdsByOption = useMemo(
        () => buildValueIdsByOption(docFilters.filters, docFilters.attributes),
        [docFilters]
    );

    const hasSelection = useMemo(() => (
        hasAnySelection(systemSelections) || hasAnySelection(filterSelections) || hasAnySelection(attributeSelections)
    ), [systemSelections, filterSelections, attributeSelections]);

    // "Remove all" needs no values, so it skips step two and confirms in a dialog.
    const handleContinue = useCallback(() => {
        if (!draftAction) return;
        if (draftAction === "remove-all") {
            setConfirmRemoveAll(true);
            return;
        }
        setSystemSelections({});
        setFilterSelections({});
        setAttributeSelections({});
        onChangeAction && onChangeAction(draftAction);
    }, [draftAction, onChangeAction]);

    const applyAction = useCallback(async (actionId) => {
        if (!actionId || !selection || applying) return;

        const selectedIds = [systemSelections, filterSelections, attributeSelections].flatMap(flattenSelectedIds);
        // In the remove step one option can stand for several tag values.
        const valueIds = (actionId === "remove")
            ? [...new Set(selectedIds.flatMap((id) => valueIdsByOption.get(id) || [id]))]
            : [...new Set(selectedIds)];

        setApplying(true);
        try {
            const { data } = await updateDocsTagsAPI({
                ...selection,
                action: actionId,
                valueIds: actionId === "remove-all" ? undefined : valueIds,
            });
            const failedCount = data?.failed?.length || 0;
            if (failedCount) {
                showToaster && showToaster("affiliate-apply-error", failureMessageOf(data.failed, `Applied to ${data?.updated || 0} file(s), couldn't update ${failedCount}.`), false);
            } else {
                showToaster && showToaster("affiliate-apply-success", "Changes applied to the selected files.", true);
            }
            onApply && onApply();
        } catch (error) {
            const isOutdated = isOutdatedSelectionError(error);
            showToaster && showToaster("affiliate-apply-error", errorMessageOf(error, isOutdated ? OUTDATED_SELECTION_MESSAGE : "Couldn't apply the changes."), false);
            if (isOutdated) onSelectionOutdated && onSelectionOutdated();
        } finally {
            setApplying(false);
            setConfirmRemoveAll(false);
        }
    }, [applying, selection, systemSelections, filterSelections, attributeSelections, valueIdsByOption, onApply, onSelectionOutdated, showToaster]);

    // A taxonomy changed in the manage dialog → reload it here + refresh the parent list.
    const onManageDialogApplied = useCallback(() => {
        loadGroups();
        onManageApplied && onManageApplied();
    }, [loadGroups, onManageApplied]);

    return (
        <div className="docs-wizard">
            <WizardDocsPanel docs={docs} count={count} note="Filters and attributes are not applicable on annex files" />

            <WizardRightPanel
                title={inValuesStep ? activeOption.title : "Affiliate filters & attributes"}
                iconSrc="/images/icon/filter.svg"
                canApply={inValuesStep ? hasSelection : !!draftAction}
                applying={applying}
                applyLabel={inValuesStep ? (activeOption.applyLabel || "APPLY") : "CONTINUE"}
                applyIconSrc={inValuesStep ? undefined : CONTINUE_ICON}
                onApply={inValuesStep ? () => applyAction(action) : handleContinue}
                headerAction={
                    <PopoverThreeDots
                        placement="bottom"
                        align="end"
                        className="affiliate-wizard-menu"
                        options={[
                            {
                                id: "manage",
                                label: "Manage the filters and attributes",
                                src: "/images/icon/settings.svg",
                                onClick: () => setManageOpen(true),
                            },
                        ]}
                    />
                }
            >
                {inValuesStep ? (
                    (isRemoveStep && docFiltersLoading) ? (
                        <PageLoader />
                    ) : (
                    <>
                        <Section
                            title="System filters"
                            info={SYSTEM_FILTERS_INFO}
                            data={isRemoveStep ? removeSystemData : systemData}
                            value={systemSelections}
                            onChange={setSystemSelections}
                        />
                        <Section
                            title="Customized filters"
                            info={CUSTOMIZED_FILTERS_INFO}
                            onManage={() => setManageOpen(true)}
                            emptyText="No available filters"
                            data={isRemoveStep ? removeFilterData : filterData}
                            value={filterSelections}
                            onChange={setFilterSelections}
                        />
                        <Section
                            title="Customized attributes"
                            info={CUSTOMIZED_ATTRIBUTES_INFO}
                            onManage={() => setManageOpen(true)}
                            emptyText="No available attributes"
                            data={isRemoveStep ? removeAttributeData : attributeData}
                            value={attributeSelections}
                            onChange={setAttributeSelections}
                        />
                    </>
                    )
                ) : (
                    <WizardActionChoice
                        options={ACTION_OPTIONS}
                        value={draftAction}
                        onChange={setDraftAction}
                    />
                )}
            </WizardRightPanel>

            <Modal
                visible={confirmRemoveAll}
                type="surprised"
                title="Remove all filters and attributes"
                extraMessage="You are about to remove all filters and attributes from the selected files."
                onClose={() => setConfirmRemoveAll(false)}
                onClickCancel={() => setConfirmRemoveAll(false)}
                onClickConfirm={() => applyAction("remove-all")}
                cancelButtonName="CANCEL"
                confirmButtonName="REMOVE"
                confirmButtonDisable={applying}
                confirmButtonLoading={applying}
                modalClass="affiliate-remove-all-confirm"
                size="large"
            />

            <ManageFiltersPanel
                visible={manageOpen}
                title="Manage filters and attributes"
                onClose={() => setManageOpen(false)}
                onApplied={onManageDialogApplied}
            />
        </div>
    );
};

const SYSTEM_FILTERS_INFO = 'System filters are provided "as is" by the system';
const CUSTOMIZED_FILTERS_INFO = "Customized filters are manageable in your organization account.";
const CUSTOMIZED_ATTRIBUTES_INFO = "Attributes are additional info which not function as filters, and can be manageable in your organization account.";

const SectionManageInfo = ({ info, onManage }) => (
    <Tooltip
        hoverInteractive={true}
        placement="top"
        className="affiliate-wizard-section-tooltip"
        content={
            <div className="affiliate-wizard-section-info">
                <p className="affiliate-wizard-section-info-text">{info}</p>
                <div className="affiliate-wizard-section-info-manage" onClick={onManage}>
                    <img src="/images/icon/settings.svg" alt="manage" />
                    <span>Manage the filters and attributes</span>
                </div>
            </div>
        }
    >
        <img src="/images/icon/info-circle.svg" alt="info" className="info-icon" />
    </Tooltip>
);

const Section = ({ title, data, value, onChange, info, onManage, emptyText }) => (
    // Sections with no groups and no empty-state text (e.g. system filters on a
    // backend without system groups) are hidden entirely.
    (!data.length && !emptyText) ? null : (
    <section className="affiliate-wizard-section">
        <div className="affiliate-wizard-section-header">
            <h3 className="affiliate-wizard-section-title">{title}</h3>
            {info && (onManage
                ? <SectionManageInfo info={info} onManage={onManage} />
                : <PopoverInfo message={info} placement="top" />
            )}
        </div>
        {data.length > 0 ? (
            <GroupedTagSelector data={data} value={value} onChange={onChange} />
        ) : (
            emptyText ? <p className="affiliate-wizard-section-empty">{emptyText}</p> : null
        )}
    </section>
    )
);

export default AffiliateFiltersWizard;
